import type { FC } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDropDownList, type DropDownItem } from '@/core/api/query/dropdown.query';
import { MenuItemType } from '@/core/api/query/menus.query';

interface ChildItemsProps {
    item: MenuItemType;
}

const ChildItems: FC<ChildItemsProps> = ({ item }) => {
    const docType = item.docType ?? '';
    const { data: { data: childItems } = { data: [], metadata: undefined } } = useDropDownList(docType);
    const navigate = useNavigate();
    const path = globalThis.location?.pathname ?? '';
    const search = globalThis.location?.search ?? '';
    const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
    const hasSubDocType = !!item.subDocType;

    // Determine which child (by docNo) is currently active from URL query params
    const columnKey = `${docType.toLowerCase()}DocNo`;
    let activeDocNo: string | null = null;

    try {
        const params = new URLSearchParams(search);
        const extra = params.get('extra');
        const filters = params.get('filters');

        if (extra) {
            const extraObj = JSON.parse(extra) as Record<string, unknown>;
            const value = extraObj[columnKey];
            if (typeof value === 'string' || typeof value === 'number') {
                activeDocNo = String(value);
            }
        }

        if (!activeDocNo && filters) {
            const filtersArr = JSON.parse(filters) as Array<{ id?: string; value?: unknown }>;
            const match = filtersArr.find((f) => f.id === columnKey);
            if (match && (typeof match.value === 'string' || typeof match.value === 'number')) {
                activeDocNo = String(match.value);
            }
        }
    } catch {
        // If parsing fails, just fall back to path-based active logic
    }

    const handleOpenCardView = (childItem: DropDownItem, targetDocType: string = docType) => {
        // Derive column name from docType, e.g. DM -> dmDocNo, TSK -> tskDocNo
        const columnKey = `${targetDocType.toLowerCase()}DocNo`;

        const extraFilters = {
            [columnKey]: childItem.value,
        };

        const params = new URLSearchParams();
        params.set('view', 'card');
        params.set('extra', JSON.stringify(extraFilters));
        navigate(`/master/${targetDocType}?${params.toString()}`);
    };

    const handleToggleSubMenu = (itemValue: string) => {
        setOpenSubMenu(openSubMenu === itemValue ? null : itemValue);
    };

    if (!childItems || childItems.length === 0) {
        return null;
    }

    return (
        <ul className="ml-4 space-y-1">
            {childItems.map((childItem) => (
                <ChildItem
                    key={childItem.value}
                    childItem={childItem}
                    parentItem={item}
                    path={path}
                    activeDocNo={activeDocNo}
                    docType={docType}
                    hasSubDocType={hasSubDocType}
                    isSubMenuOpen={openSubMenu === childItem.value}
                    onToggleSubMenu={() => handleToggleSubMenu(childItem.value)}
                    onOpenCardView={handleOpenCardView}
                />
            ))}
        </ul>
    );
};

interface ChildItemProps {
    childItem: DropDownItem;
    parentItem: MenuItemType;
    path: string;
    activeDocNo: string | null;
    docType: string;
    hasSubDocType: boolean;
    isSubMenuOpen: boolean;
    onToggleSubMenu: () => void;
    onOpenCardView: (childItem: DropDownItem, targetDocType?: string) => void;
}

const ChildItem: FC<ChildItemProps> = ({
    childItem,
    parentItem,
    path,
    activeDocNo,
    docType,
    hasSubDocType,
    isSubMenuOpen,
    onToggleSubMenu,
    onOpenCardView,
}) => {
    const navigate = useNavigate();
    const target = `/master/${docType}`;
    const isActive =
        path.startsWith(target) &&
        (!!activeDocNo ? activeDocNo === String(childItem.value) : false);

    const handleWorkItemsClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (!parentItem.subDocType) return;

        const columnKey = `${docType.toLowerCase()}DocNo`;
        const extraFilters = {
            [columnKey]: childItem.value,
        };

        const params = new URLSearchParams();
        params.set('view', 'card');
        params.set('extra', JSON.stringify(extraFilters));
        navigate(`/master/${parentItem.subDocType}?${params.toString()}`);
    };

    return (
        <li className="space-y-1">
            <div className="flex items-center gap-2">
                <Link
                    to={target}
                    className="flex-1 flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-all duration-200"
                    style={{
                        color: isActive ? 'var(--mantine-color-brand-6)' : 'var(--app-text-muted)',
                        backgroundColor: isActive ? 'var(--app-surface-muted)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                        if (isActive) return;
                        e.currentTarget.style.backgroundColor = 'var(--app-surface-muted)';
                        e.currentTarget.style.color = 'var(--mantine-color-brand-6)';
                    }}
                    onMouseLeave={(e) => {
                        if (isActive) {
                            e.currentTarget.style.backgroundColor = 'var(--app-surface-muted)';
                            e.currentTarget.style.color = 'var(--mantine-color-brand-6)';
                            return;
                        }
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--app-text-muted)';
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        onOpenCardView(childItem);
                    }}
                >
                    <span className="font-medium">{childItem.label}</span>
                </Link>

                {hasSubDocType && (
                    <button
                        onClick={onToggleSubMenu}
                        className="flex items-center justify-center rounded-lg px-2 py-1.5 text-xs transition-all duration-200"
                        style={{
                            color: isSubMenuOpen ? 'var(--mantine-color-brand-6)' : 'var(--app-text-muted)',
                        }}
                        aria-label="Toggle work items"
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--app-surface-muted)';
                            e.currentTarget.style.color = 'var(--mantine-color-brand-6)';
                        }}
                        onMouseLeave={(e) => {
                            if (isSubMenuOpen) {
                                e.currentTarget.style.backgroundColor = 'var(--app-surface-muted)';
                                e.currentTarget.style.color = 'var(--mantine-color-brand-6)';
                                return;
                            }
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--app-text-muted)';
                        }}
                    >
                        <svg
                            className={`w-3 h-3 transition-transform duration-200 ${isSubMenuOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 9l-7 7-7-7"
                            />
                        </svg>
                    </button>
                )}
            </div>

            {hasSubDocType && isSubMenuOpen && (
                <ul className="ml-4 space-y-0.5">
                    <li>
                        <Link
                            to="#"
                            onClick={handleWorkItemsClick}
                            className="flex items-center gap-2 rounded-lg px-2 py-1 text-xs transition-all duration-200"
                            style={{
                                color: 'var(--app-text-muted)',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--app-surface-muted)';
                                e.currentTarget.style.color = 'var(--mantine-color-brand-6)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = 'var(--app-text-muted)';
                            }}
                        >
                            <span className="font-medium">Work Items</span>
                        </Link>
                    </li>
                </ul>
            )}
        </li>
    );
};

export default ChildItems;